import React from 'react';
import { ViewableMultiSubmission, formatBytes } from './submissionMedia';
import '../../pages/Evaluations.css';

function renderManifestValue(value: unknown): React.ReactNode {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return (
        <a href={trimmed} target="_blank" rel="noopener noreferrer">
          {trimmed}
        </a>
      );
    }
    return trimmed || '-';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '-';
    return (
      <ul className="evaluations-multi-list">
        {value.map((entry, idx) => {
          if (typeof entry === 'string') {
            return <li key={`multi-value-${idx}`}>{entry}</li>;
          }
          if (entry && typeof entry === 'object') {
            const maybeObj = entry as { name?: string; value?: string };
            return <li key={`multi-value-${idx}`}>{maybeObj.name || maybeObj.value || JSON.stringify(maybeObj)}</li>;
          }
          return <li key={`multi-value-${idx}`}>{String(entry)}</li>;
        })}
      </ul>
    );
  }
  if (value && typeof value === 'object') return JSON.stringify(value);
  return '-';
}

const MultiPackageView: React.FC<{ submission: ViewableMultiSubmission }> = ({ submission }) => {
  const manifest = submission.manifest;
  const formFields = manifest?.form?.fields || manifest?.fields || [];
  const formValues = manifest?.form?.values || manifest?.values || {};
  const artifactsRaw = manifest?.artifacts;
  const artifacts = Array.isArray(artifactsRaw) ? artifactsRaw : [];

  return (
    <div className="evaluations-multi-card">
      <div className="evaluations-multi-meta">
        <span>Schema: {manifest?.schema || 'ikompete.multi.v1'}</span>
        {typeof submission.eval_count === 'number' && (
          <span>Evaluations so far: {submission.eval_count}</span>
        )}
        {submission.already_scored && (
          <span className="evaluations-multi-pill">Already scored by you</span>
        )}
        <a href={submission.url} target="_blank" rel="noopener noreferrer">
          Open raw manifest
        </a>
      </div>

      {Array.isArray(formFields) && formFields.length > 0 && (
        <div className="evaluations-multi-section">
          <h3>Form Data</h3>
          <div className="evaluations-multi-grid">
            {formFields.map((field, idx) => {
              const fieldValue = field.value ?? formValues[field.id || ''];
              return (
                <div key={`${field.id || 'field'}-${idx}`} className="evaluations-multi-item">
                  <div className="evaluations-multi-item__label">{field.label || field.id || 'Field'}</div>
                  <div className="evaluations-multi-item__value">{renderManifestValue(fieldValue)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="evaluations-multi-section">
        <h3>Artifacts</h3>
        {artifacts.length > 0 ? (
          <div className="evaluations-multi-artifacts">
            {artifacts.map((artifact, idx) => (
              <div key={`${artifact.object_key || artifact.stored_name || 'artifact'}-${idx}`} className="evaluations-multi-artifact">
                <div>
                  <strong>{artifact.original_name || artifact.stored_name || 'File'}</strong>
                  <div className="evaluations-multi-artifact__meta">
                    <span>{artifact.content_type || 'application/octet-stream'}</span>
                    <span>{formatBytes(artifact.size_bytes)}</span>
                  </div>
                </div>
                {artifact.url ? (
                  <a href={artifact.url} target="_blank" rel="noopener noreferrer">
                    Open
                  </a>
                ) : (
                  <span>Unavailable</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="evaluations-empty-text">No files uploaded in this package.</p>
        )}
      </div>
    </div>
  );
};

export default MultiPackageView;
